/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';

import {
  SelectField, useRecordContext,
} from 'react-admin';

import {
  Grid,
  Box,
  Typography,
  CardContent,
  Card,
} from '@mui/material';
import { get } from 'lodash';
import Answer from '../Answer';

import languages, { Languages } from '../../../../constants/languages';
import booleanChoices from '../../../../constants/booleanChoices';
import { countryChoices } from '../../../../constants/countries';
import { pepRelationChoices } from '../../../../constants/pepRelations';
import { pepRoleChoices } from '../../../../constants/pepRoles';
import conflictFields from '../../../../constants/conflictFields';
import applyChoices from '../../applyChoices';
import { PepParameters } from '../../questionParameters';

const PEP_TITLE = {
  [languages.EN]: 'Politically exposed person',
  [languages.SV]: 'Politiskt utsatt ställning?',
  [languages.DE]: 'Politisch exponierte Person',
} as const;

const IS_PEP_TITLE = {
  [languages.EN]: 'Is the person or someone close to them politically exposed?',
  [languages.SV]: 'Är du eller någon i din närhet en person i politiskt utsatt ställning?',
  [languages.DE]: 'Ist die Person oder eine ihr nahestehende Person politisch exponiert?',
} as const;

const PEP_POSITION_TITLE = {
  [languages.EN]: 'PEP position',
  [languages.SV]: 'PEP position',
  [languages.DE]: 'PEP-Position',
} as const;

const PEP_RELATION_TITLE = {
  [languages.EN]: 'Relation to the politically exposed person',
  [languages.SV]: 'Vilken relation har du till personen?',
  [languages.DE]: 'Beziehung zur politisch exponierten Person',
} as const;

const PEP_COUNTRY_TITLE = {
  [languages.EN]: 'In which country is the role held that entails PEP status',
  [languages.SV]: 'I vilket land innehas rollen som medför PEP status',
  [languages.DE]: 'In welchem Land wird die Rolle ausgeübt, die den PEP-Status begründet?',
} as const;

const ValueField = ({
  language,
  md = 6,
  boldFont = true,
  extraParameters,
}: {
  language?: Languages;
  md?: number;
  boldFont?: boolean;
  extraParameters?: PepParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value');
  const sx = get(record, 'sx', {});

  if (!language) return null;

  const fields = extraParameters?.fields;

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">{fields?.isPep?.label ?? IS_PEP_TITLE[language]}</Typography>
          <SelectField
            record={value}
            fontWeight={boldFont ? 'bold' : ''}
            choices={applyChoices(
              booleanChoices[language],
              fields?.isPep?.options,
              fields?.isPep?.choiceLabels,
            )}
            source="isPep"
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">{fields?.position?.label ?? PEP_POSITION_TITLE[language]}</Typography>
          <SelectField
            fontWeight={boldFont ? 'bold' : ''}
            record={value}
            choices={applyChoices(
              pepRoleChoices(language),
              fields?.position?.options,
              fields?.position?.choiceLabels,
            )}
            source="pep.role"
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">{fields?.relation?.label ?? PEP_RELATION_TITLE[language]}</Typography>
          <SelectField
            fontWeight={boldFont ? 'bold' : ''}
            record={value}
            choices={applyChoices(
              pepRelationChoices(language),
              fields?.relation?.options,
              fields?.relation?.choiceLabels,
            )}
            source="pep.relation"
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">{fields?.country?.label ?? PEP_COUNTRY_TITLE[language]}</Typography>
          <SelectField
            fontWeight={boldFont ? 'bold' : ''}
            record={value}
            choices={applyChoices(
              countryChoices(language),
              fields?.country?.options,
              fields?.country?.choiceLabels,
            )}
            source="pep.country"
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
    </>
  );
};

const Pep = ({ extraParameters }: { extraParameters?: PepParameters }) => {
  const record = useRecordContext();

  const language = record?.language as Languages ?? languages.EN;

  const value = get(record, 'pep', get(record, 'value'));

  if (!value || !language) return <Typography>-</Typography>;

  return (
    <Answer
      label={extraParameters?.label ?? PEP_TITLE[language]}
      source="pep"
      datapoint={conflictFields.PEP}
      identificationInfo={undefined}
      isComplexType
    >
      <Card variant="outlined" sx={{ m: 0 }}>
        <CardContent sx={{ p: 4 }}>
          <Grid size={12}>
            <Grid container spacing={2}>
              <ValueField language={language} extraParameters={extraParameters} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Answer>
  );
};

Pep.ValueField = ({
  language,
  extraParameters,
}: {
  language?: Languages;
  extraParameters?: PepParameters;
}) => (
  <Grid size={12}>
    <Grid container spacing={2}>
      <ValueField language={language} extraParameters={extraParameters} />
    </Grid>
  </Grid>
);

Pep.Nested = ({ extraParameters }: { extraParameters?: PepParameters }) => {
  const record = useRecordContext();

  const language = record?.language as Languages ?? languages.EN;

  return (
    <ValueField language={language} md={3} boldFont={false} extraParameters={extraParameters} />
  );
};

export default Pep;
